import React, { createContext, RefObject, useContext } from 'react';
import { Task } from '../../types';
import { DraftTaskSelection, DraggingTask, PlannerTaskInfo, ResizingTask, TimelineScale } from './types';

export interface TimelineViewProps {
    activeTimelineLabel: string;
    activeTimelineDateKey: string;
    addTaskDate: Date | null;
    addTaskDuration: number;
    addTaskTime: string | null;
    currentMouseY: number | null;
    dragStartHour: number | null;
    dragStartOffset: number;
    dragTaskStartTopY: number | null;
    dragTaskStartY: number | null;
    draggingTask: DraggingTask | null;
    draftSelection: DraftTaskSelection | null;
    getCollectionName: (collectionId: string) => string;
    getFolderName: (folderId: string) => string;
    getTasksForDay: (date: Date) => PlannerTaskInfo[];
    getTasksForMonth: (date: Date) => PlannerTaskInfo[];
    getTasksForYear: (year: number) => PlannerTaskInfo[];
    getTimelineDateFromClientY: (clientY: number) => Date | null;
    isDragging: boolean;
    onCancelDraftSelection: () => void;
    onCreateTimedTask: (date: Date) => void;
    onCreateFlexibleTask: (date: Date, precision: 'day' | 'month' | 'year') => void;
    onGoToNow: () => void;
    onOpenSchedule: () => void;
    onOpenDayInDaysView: (date: Date) => void;
    onMoveTaskToDate: (taskInfo: PlannerTaskInfo, date: Date) => void;
    onGoToNextTask: () => void;
    onOpenTask: (taskInfo: PlannerTaskInfo) => void;
    onPickExistingTask: (date?: Date) => void;
    onRequestTaskListReturn: (scope: 'day' | 'month' | 'year', date: Date) => void;
    onTaskListOpened: () => void;
    onZoomOutToParent: (scale: TimelineScale) => void;
    onConfirmDraftSelection: () => void;
    onDraftPointerDown: (e: React.PointerEvent) => void;
    onDraftResizePointerDown: (edge: 'top' | 'bottom', e: React.PointerEvent) => void;
    onDeleteTask: (taskId: string, collectionId: string) => void;
    onEditTask: (task: Task, collectionId: string) => void;
    onHourPointerDown: (date: Date, hour: number, e: React.PointerEvent) => void;
    onResizeLongPress: (task: Task, collectionId: string, edge: 'top' | 'bottom', element: HTMLElement, clientY: number, pointerId: number) => void;
    onScaleChange: (scale: TimelineScale, anchorDate?: Date) => void;
    onShowCompletedTasksChange: (show: boolean) => void;
    onSelectedDateChange: (date: Date) => void;
    onTaskLongPress: (task: Task, collectionId: string, element: HTMLElement, clientY: number, pointerId: number) => void;
    onToggleComplete: (taskId: string, collectionId: string) => void;
    resizingTask: ResizingTask | null;
    resizeStartY: number | null;
    scrollContainerRef: RefObject<HTMLDivElement>;
    selectedDate: Date;
    showCompletedTasks: boolean;
    tasksWithDueDates: PlannerTaskInfo[];
    taskListToOpen: { scope: 'day' | 'month' | 'year'; date: Date } | null;
    timelineScale: TimelineScale;
    timelineWindow: { start: number; end: number };
    timeSlotHeight: number;
}

interface TimelineMenuState {
    creationMenuKey: string | null;
    creationMenuRef: React.RefObject<HTMLDivElement>;
    setCreationMenuKey: React.Dispatch<React.SetStateAction<string | null>>;
    timelineZoomAnchorDate: Date | null;
}

type TimelineContextValue = TimelineViewProps & TimelineMenuState;
const TimelineContext = createContext<TimelineContextValue | null>(null);

export const TimelineViewProvider: React.FC<React.PropsWithChildren<TimelineContextValue>> = ({ children, ...value }) => (
    <TimelineContext.Provider value={value}>{children}</TimelineContext.Provider>
);

export const useTimelineView = () => {
    const value = useContext(TimelineContext);
    if (!value) throw new Error('useTimelineView must be used inside TimelineViewProvider');
    return value;
};
